import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where,
  type Timestamp,
} from 'firebase/firestore';
import type { User } from 'firebase/auth';
import { appConfig } from '@/config/app';
import { USERS_COLLECTION } from '@/services/auth/constants';
import { getFirestoreDb } from '@/services/firebase';
import type {
  AuthProvider,
  UserProfile,
  UserProfileInput,
  UserRole,
} from '@/types/user';
import { recordEntityWrite, toAuditSnapshot } from '@/services/audit';
import { withFirestoreError } from '@/shared/utils/firestoreError';

type UserFirestoreDoc = {
  email: string;
  displayName: string;
  photoURL: string | null;
  companyId: string;
  role: UserRole;
  active: boolean;
  provider: AuthProvider;
  /** @deprecated Migrar a `active` — solo lectura legacy */
  status?: 'active' | 'disabled';
  createdAt: Timestamp;
  updatedAt: Timestamp;
};

function toDate(value: Timestamp): Date {
  return value.toDate();
}

function resolveActive(data: Partial<UserFirestoreDoc>): boolean {
  if (typeof data.active === 'boolean') return data.active;
  return data.status === 'active';
}

function mapDoc(uid: string, data: Partial<UserFirestoreDoc>): UserProfile {
  const now = new Date();
  return {
    uid,
    email: data.email ?? '',
    displayName: data.displayName ?? '',
    photoURL: data.photoURL ?? null,
    companyId: data.companyId ?? appConfig.companyId,
    role: data.role ?? 'pending',
    active: resolveActive(data),
    provider: data.provider ?? 'email',
    createdAt: data.createdAt ? toDate(data.createdAt) : now,
    updatedAt: data.updatedAt ? toDate(data.updatedAt) : now,
  };
}

function isGoogleUser(user: User): boolean {
  return user.providerData.some((p) => p.providerId === 'google.com');
}

export async function getUserProfile(uid: string): Promise<UserProfile | null> {
  const snap = await getDoc(doc(getFirestoreDb(), USERS_COLLECTION, uid));
  if (!snap.exists()) return null;
  return mapDoc(snap.id, snap.data() as UserFirestoreDoc);
}

export async function listUsersByCompany(
  companyId: string = appConfig.companyId,
): Promise<UserProfile[]> {
  const q = query(
    collection(getFirestoreDb(), USERS_COLLECTION),
    where('companyId', '==', companyId),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => mapDoc(d.id, d.data() as UserFirestoreDoc));
}

/** Crea perfil pendiente para primer ingreso con Google. */
export async function createPendingGoogleProfile(
  user: User,
): Promise<UserProfile> {
  const ref = doc(getFirestoreDb(), USERS_COLLECTION, user.uid);
  const payload = {
    email: (user.email ?? '').trim().toLowerCase(),
    displayName: user.displayName ?? user.email ?? 'Usuario',
    photoURL: user.photoURL,
    companyId: appConfig.companyId,
    role: 'pending' as const,
    active: false,
    provider: 'google' as const,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  await setDoc(ref, payload);
  const saved = await getDoc(ref);
  if (!saved.exists()) {
    throw new Error('No se pudo crear el perfil de usuario.');
  }
  return mapDoc(saved.id, saved.data() as UserFirestoreDoc);
}

/** Sincroniza nombre/foto desde Firebase Auth (sin cambiar rol ni active). */
export async function syncProfileFromAuth(user: User): Promise<void> {
  const ref = doc(getFirestoreDb(), USERS_COLLECTION, user.uid);
  await setDoc(
    ref,
    {
      email: (user.email ?? '').trim().toLowerCase(),
      displayName: user.displayName ?? '',
      photoURL: user.photoURL,
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );
}

/**
 * Tras login: obtiene perfil, crea pendiente si es Google nuevo, o sincroniza metadata.
 */
export async function ensureUserProfile(user: User): Promise<UserProfile | null> {
  const existing = await getUserProfile(user.uid);
  if (existing) {
    if (isGoogleUser(user)) {
      await syncProfileFromAuth(user);
      return getUserProfile(user.uid);
    }
    return existing;
  }
  if (isGoogleUser(user)) {
    return createPendingGoogleProfile(user);
  }
  return null;
}

export async function saveUserProfile(input: UserProfileInput): Promise<UserProfile> {
  return withFirestoreError(async () => {
  const ref = doc(getFirestoreDb(), USERS_COLLECTION, input.uid);
  const existing = await getDoc(ref);
  const before = existing.exists()
    ? toAuditSnapshot(mapDoc(existing.id, existing.data() as UserFirestoreDoc))
    : undefined;
  const payload = {
    email: input.email.trim().toLowerCase(),
    displayName: input.displayName.trim(),
    photoURL: input.photoURL ?? null,
    companyId: input.companyId,
    role: input.role,
    active: input.active,
    provider: input.provider ?? 'email',
    updatedAt: serverTimestamp(),
    ...(existing.exists() ? {} : { createdAt: serverTimestamp() }),
  };
  await setDoc(ref, payload, { merge: true });
  const saved = await getDoc(ref);
  if (!saved.exists()) {
    throw new Error('No se pudo guardar el perfil de usuario.');
  }
  const result = mapDoc(saved.id, saved.data() as UserFirestoreDoc);
  recordEntityWrite({
    entityType: 'user',
    entityId: result.uid,
    isCreate: !existing.exists(),
    ...(before !== undefined ? { before } : {}),
    after: toAuditSnapshot(result),
  });
  return result;
  }, 'user');
}
